import React, {useEffect, useMemo} from 'react'
import {VStack} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {SpecialOffer} from './index'
import {useCommerceAPI} from '../../commerce-api/contexts'
import useBasket from '../../commerce-api/hooks/useBasket'
import useAsync from '../../commerce-api/hooks/useAsync'

/**
 * Extracts promotion identifiers associated with the passed product.
 * @param {array} bonusDiscountLineItems is the basket with all promotions
 * @param {object} bonusPromotion is the bonus promotion that should be rendered separately
 * @param {object} product is the product where the promotions should be applied
 * @param {string} variant is the variant of the product
 * @return array of promotion identifiers
 */
const getPromotionIds = function (bonusDiscountLineItems, bonusPromotion, product, variant) {
    if (!bonusPromotion) {
        return product?.productPromotions
            ?.map(({promotionId}) => promotionId)
            ?.filter((promotionId) => {
                if (variant === 'bonus') {
                    // we should restrict filtering by bonus promotions
                    if (!bonusDiscountLineItems) {
                        return false
                    }
                    return (
                        bonusDiscountLineItems.filter((bonus) => bonus.promotionId === promotionId)
                            .length !== 0
                    )
                }

                return true
            })
    }
    return [bonusPromotion.promotionId]
}

export const useProductSpecialOffers = (
    product,
    bonusPromotion,
    variant,
    {isLoading: isProductLoading, promotions: preloadedPromotions} = {}
) => {
    const api = useCommerceAPI()
    const basket = useBasket()
    const {data, execute, loading, reset} = useAsync(api.shopperPromotions.getPromotions)

    const promotionIds = useMemo(() => {
        if (isProductLoading) return []
        const ids = getPromotionIds(
            basket?.bonusDiscountLineItems,
            bonusPromotion,
            product,
            variant
        )
        return ids
    }, [product, bonusPromotion, basket?.bonusDiscountLineItems, variant, isProductLoading])

    const missingPromotionIds = useMemo(
        () =>
            promotionIds?.filter(
                (promotionId) =>
                    promotionId && !preloadedPromotions?.find(({id}) => id === promotionId)
            ),
        [promotionIds, preloadedPromotions]
    )

    const missingPromotionsIdParam = useMemo(
        () => missingPromotionIds?.join(','),
        [missingPromotionIds]
    )
    useEffect(() => {
        if (missingPromotionsIdParam) {
            execute({
                parameters: {ids: missingPromotionsIdParam}
            })
        } else {
            reset()
        }
    }, [execute, missingPromotionsIdParam, reset])

    const promotions = useMemo(() => {
        if (!promotionIds) {
            return []
        }
        const promotionResults = [...(preloadedPromotions || []), ...(data?.data || [])].filter(
            ({id}) => promotionIds.includes(id)
        )
        if (variant === 'bonus') {
            if (bonusPromotion && promotionResults?.length) {
                const bonusLineItemId = bonusPromotion.id
                promotionResults[0].productItem = basket?.productItems.filter(
                    (item) => item?.bonusDiscountLineItemId === bonusLineItemId
                )
            }
            return promotionResults.filter(({c_discountProducts}) => c_discountProducts)
        } else {
            return promotionResults
        }
    }, [bonusPromotion, data, promotionIds, variant, basket?.productItems, preloadedPromotions])

    return {product, variant, promotions, loading: loading || isProductLoading}
}

const ProductSpecialOffers = ({product, variant, promotions, ...props}) => {
    if (!promotions?.length) {
        return null
    }

    return (
        <VStack spacing="10px">
            {promotions.map((promotion) => (
                <SpecialOffer
                    key={promotion.id}
                    promotion={promotion}
                    product={product}
                    variant={variant || 'default'}
                    {...props}
                />
            ))}
        </VStack>
    )
}

ProductSpecialOffers.propTypes = {
    product: PropTypes.object,
    variant: PropTypes.oneOf(['default', 'bonus']),
    promotions: PropTypes.array
}
export default ProductSpecialOffers
